import {
  EApplicantType,
  ECreditApplicationStep,
  EDocumentStatus,
  EDocumentType,
  EExternalStep,
  EExternalStepStatus,
} from '@if/core/types'

import { CreditApplicationDocument, EMPTY_ARRAY, FilteredApplicant, FilteredCreditApplication, RequiredDocument, RequiredDocumentWithStatus } from '@applyonline/shared/types'
import { Constants } from '../types/constants'
import calculateMonthlyAmount from './compute-selectors'

const APPLICANT_TYPE = EApplicantType.Applicant

export function isDocumentMatchingRequiredDoc(document: CreditApplicationDocument, reqDoc: RequiredDocument) {
  return (
    document.typeId === reqDoc.typeId &&
    document.applicantType === reqDoc.applicantType &&
    document.subKey === reqDoc.subKey
  )
}

export const manualUploadNotAllowedDocsTypes = new Set([
  EDocumentType.SixMonthsPersonalBankStatements,
  EDocumentType.VoidCheque,
  EDocumentType.ThreeMonthsPersonalBankStatements,
  EDocumentType.LessThanThreeMonthsPersonalBankStatements,
  EDocumentType.SignedCVT,
  EDocumentType.AuditCVT,
])

export function hasMissingDocuments(
  creditApp: FilteredCreditApplication | null,
  forStep?: ECreditApplicationStep,
): boolean {
  if (!creditApp) return false

  const reqDocs = forStep
    ? creditApp.requiredDocuments.filter((x) => x.requiredBeforeStep === forStep)
    : creditApp.requiredDocuments

  return !reqDocs.every(
    (req) =>
      manualUploadNotAllowedDocsTypes.has(req.typeId) ||
      creditApp.documents.findIndex(
        (doc) => isDocumentMatchingRequiredDoc(doc, req) && !Constants.ExpectingDocumentStatuses.includes(doc.status),
      ) > -1,
  )
}
export function getLoanAmount(creditApp: FilteredCreditApplication) {
  if (creditApp?.finalDecision) {
    let amount = Math.min(creditApp.requestedLoanAmount, creditApp.finalDecision.maxAmountFinanced)

    if (creditApp.worksheet !== null) amount = Math.min(amount, creditApp.worksheet.amountRequested)
    return amount
  }
  return 0
}

export const getWorksheet = (creditApp: FilteredCreditApplication) => {
  if (creditApp.worksheet === null) {
    throw new Error('Worksheet is null')
  }
  return creditApp.worksheet
}

export function getMaxPaymentForRequestedAmount(
  creditApplication: FilteredCreditApplication | null,
  maxLoanTerm: number,
) {
  if (creditApplication?.prequalificationDecision) {
    const amount = Math.min(
      creditApplication?.requestedLoanAmount ?? Number.MAX_VALUE,
      creditApplication?.prequalificationDecision.maxLoanAmount ?? Number.MAX_VALUE,
    )
    return calculateMonthlyAmount(
      maxLoanTerm,
      creditApplication?.prequalificationDecision.maxInterestRate > 0
        ? creditApplication?.prequalificationDecision.maxInterestRate
        : Constants.MaxRate,
      amount,
      Constants.FinanceFeeRateForRegular,
    )
  }
  return 0
}

export function getMinPaymentForRequestedAmount(
  creditApplication: FilteredCreditApplication | null,
  maxLoanTerm: number,
) {
  if (creditApplication?.prequalificationDecision) {
    const amount = Math.min(
      creditApplication?.requestedLoanAmount ?? Number.MAX_VALUE,
      creditApplication?.prequalificationDecision.maxLoanAmount ?? Number.MAX_VALUE,
    )
    return calculateMonthlyAmount(
      maxLoanTerm,
      creditApplication?.prequalificationDecision.minInterestRate,
      amount,
      Constants.FinanceFeeRateForRegular,
    )
  }
  return 0
}
const stepsBeforeOrEqualCVT = new Set<ECreditApplicationStep>([
  ECreditApplicationStep.Credit,
  ECreditApplicationStep.Financing,
  ECreditApplicationStep.CVT,
])

export function areAllDocumentsReadyForCVT(requiredDocumentsWithStatus: RequiredDocumentWithStatus[]): boolean {
  return requiredDocumentsWithStatus.every(
    (d) => !stepsBeforeOrEqualCVT.has(d.requiredBeforeStep) || d.status === EDocumentStatus.Approved,
  )
}
export function getPrequalifiedAmount(creditApp: FilteredCreditApplication) {
  return Math.min(creditApp.requestedLoanAmount, creditApp.prequalificationDecision?.maxLoanAmount ?? Number.MAX_VALUE)
}

export function mustAskApplicantSIN(filteredCreditApplication: FilteredCreditApplication) {
  return !filteredCreditApplication.applicant.hasSin && filteredCreditApplication.applicant.isNewToCredit
}

export const hasSignedContract = (creditApp: FilteredCreditApplication | null): boolean => {
  if (!creditApp) return false

  return creditApp.documents.some(
    (x) =>
      x.typeId === EDocumentType.SignedCVT &&
      (x.status === EDocumentStatus.AwaitingApproval || x.status === EDocumentStatus.Approved),
  )
}

export const hasApprovedCVT = (creditApp: FilteredCreditApplication | null): boolean => {
  if (!creditApp) return false

  return creditApp.documents.some((x) => x.typeId === EDocumentType.SignedCVT && x.status === EDocumentStatus.Approved)
}

export function buildRequiredDocumentsWithStatus(
  requiredDocuments: RequiredDocument[] | undefined | null,
  documents: CreditApplicationDocument[] | undefined | null,
) {
  if (!requiredDocuments || !documents) return EMPTY_ARRAY

  const ret = [] as RequiredDocumentWithStatus[]
  requiredDocuments.forEach((req) => {
    const doc = documents.find((d) => isDocumentMatchingRequiredDoc(d, req))
    if (doc) ret.push({ ...req, status: doc.status, refusalReason: doc.refusalReason } as RequiredDocumentWithStatus)
    else ret.push({ ...req, status: EDocumentStatus.AwaitingDocument, refusalReason: null })
  })

  return ret
}

export function applicantFullName(applicant: FilteredApplicant): string {
  return `${applicant.firstName}\u00a0${applicant.lastName}`
}

export function hasApplicantSubmittedAllRequiredIncomeDocuments(
  reqDocWithStatus: RequiredDocumentWithStatus[],
): boolean {
  const applicantIncomeDocs = reqDocWithStatus.filter(
    (x) => x.applicantType === APPLICANT_TYPE && Constants.IncomeProofTypes.includes(x.typeId),
  )
  if (applicantIncomeDocs.length === 0) return false

  const missing = applicantIncomeDocs.filter((x) => Constants.ExpectingDocumentStatuses.includes(x.status))

  return missing.length === 0
}

export function hasAnyExpectingRequiredIncomeDoc(creditApp: FilteredCreditApplication | null): boolean {
  const reqDocsWithStatus = buildRequiredDocumentsWithStatus(creditApp?.requiredDocuments, creditApp?.documents)
  return reqDocsWithStatus.some(
    (x) =>
      x.applicantType === APPLICANT_TYPE &&
      Constants.IncomeProofTypes.includes(x.typeId) &&
      Constants.ExpectingDocumentStatuses.includes(x.status),
  )
}

export function hasApplicantCompletedFlinks(creditApp: FilteredCreditApplication | null) {
  if (creditApp) {
    const reqStep = creditApp.requiredExternalSteps.find(
      (x) =>
        x.applicantType === APPLICANT_TYPE &&
        x.externalStepId === EExternalStep.BankAccount &&
        x.status === EExternalStepStatus.Completed,
    )

    if (reqStep) return true
  }

  return false
}

export function bankStatementOrVoidCheckAreDeclined(creditApp: FilteredCreditApplication | null) {
  const requiredDocuments = buildRequiredDocumentsWithStatus(creditApp?.requiredDocuments, creditApp?.documents)
  const docTypeSet = new Set([
    EDocumentType.SixMonthsPersonalBankStatements,
    EDocumentType.VoidCheque,
    EDocumentType.ThreeMonthsPersonalBankStatements,
    EDocumentType.LessThanThreeMonthsPersonalBankStatements,
  ])
  return requiredDocuments.some((x) => docTypeSet.has(x.typeId) && x.status === EDocumentStatus.Refused)
}

export function applicantIncomeAccepted(creditApp: FilteredCreditApplication | null) {
  return (
    creditApp?.requiredExternalSteps.some(
      (x) =>
        x.applicantType === EApplicantType.Applicant &&
        x.externalStepId === EExternalStep.BankAccount &&
        x.status === EExternalStepStatus.Completed,
    ) && !bankStatementOrVoidCheckAreDeclined(creditApp)
  )
}

export function hasApplicantSubmittedFlinks(creditApp: FilteredCreditApplication | null) {
  const inProgress = creditApp?.requiredExternalSteps.some(
    (x) =>
      x.applicantType === EApplicantType.Applicant &&
      x.externalStepId === EExternalStep.BankAccount &&
      (x.status === EExternalStepStatus.InProgress || x.status === EExternalStepStatus.AnalysisInProgress),
  )

  return inProgress ?? false
}

export function areApplicantIncomesUnderReview(creditApp: FilteredCreditApplication): boolean {
  const areIncomesConfirmed = creditApp.applicant.areIncomesConfirmed

  return (
    !areIncomesConfirmed &&
    (hasApplicantSubmittedFlinks(creditApp) ||
      (creditApp.applicant.areFlinksIncomeMatchingDeclared &&
        !bankStatementOrVoidCheckAreDeclined(creditApp) &&
        !hasAnyExpectingRequiredIncomeDoc(creditApp)))
  )
}

export function areAllRequiredDocumentsApproved(requiredDocumentWithStatusList: RequiredDocumentWithStatus[]): boolean {
  const docIgnored = new Set([EDocumentType.SignedCVT, EDocumentType.AuditCVT])
  return requiredDocumentWithStatusList.every((d) => docIgnored.has(d.typeId) || d.status === EDocumentStatus.Approved)
}

export function applicantBankAccountStatusMatch(
  creditApp: FilteredCreditApplication | null,
  status: EExternalStepStatus,
) {
  const match = creditApp?.requiredExternalSteps.some(
    (x) =>
      x.applicantType === EApplicantType.Applicant &&
      x.externalStepId === EExternalStep.BankAccount &&
      x.status === status,
  )

  return match ?? false
}

export function applicantFlinksId(creditApp: FilteredCreditApplication | null) {
  if (creditApp) {
    const reqStep = creditApp.requiredExternalSteps.find(
      (x) => x.applicantType === EApplicantType.Applicant && x.externalStepId === EExternalStep.BankAccount,
    )

    if (reqStep) {
      return reqStep.externalServiceId
    }
  }
  return ''
}
